// Pooled particle system. Cheap white circles, tinted and scaled per burst, so
// hits, pickups, build puffs, and wave clears all throw satisfying debris with
// near-zero allocation after warmup.

import { Container, Graphics } from 'pixi.js';

// A soft, constant ambient draft (px/s) applied to every live particle, so
// smoke, dust, and debris all drift the same way and the island feels breathed
// on. Small enough to be a subtle wind, not a shove.
const WIND_DRIFT = 7;

interface Particle {
  gfx: Graphics;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  gravity: number;
  spin: number;
  baseScale: number;
  active: boolean;
}

export interface BurstOptions {
  count?: number;
  color?: number;
  speed?: number; // base outward speed (px/s)
  spread?: number; // velocity randomness
  life?: number; // seconds
  gravity?: number; // downward accel (px/s^2)
  size?: number; // base radius scale
}

export class ParticleSystem {
  private pool: Particle[] = [];

  constructor(private readonly layer: Container) {}

  private obtain(): Particle {
    for (const p of this.pool) {
      if (!p.active) return p;
    }
    const gfx = new Graphics();
    gfx.circle(0, 0, 4).fill({ color: 0xffffff });
    gfx.visible = false;
    this.layer.addChild(gfx);
    const p: Particle = {
      gfx,
      vx: 0,
      vy: 0,
      life: 0,
      maxLife: 1,
      gravity: 0,
      spin: 0,
      baseScale: 1,
      active: false,
    };
    this.pool.push(p);
    return p;
  }

  burst(x: number, y: number, opts: BurstOptions = {}): void {
    const count = opts.count ?? 8;
    const color = opts.color ?? 0xffffff;
    const speed = opts.speed ?? 90;
    const spread = opts.spread ?? 60;
    const life = opts.life ?? 0.6;
    const gravity = opts.gravity ?? 220;
    const size = opts.size ?? 1;

    for (let i = 0; i < count; i++) {
      const p = this.obtain();
      const angle = Math.random() * Math.PI * 2;
      const mag = speed + (Math.random() - 0.5) * spread;
      p.vx = Math.cos(angle) * mag;
      p.vy = Math.sin(angle) * mag - speed * 0.4; // bias slightly upward
      p.life = life * (0.7 + Math.random() * 0.6);
      p.maxLife = p.life;
      p.gravity = gravity;
      p.spin = (Math.random() - 0.5) * 8;
      p.baseScale = size * (0.6 + Math.random() * 0.8);
      p.active = true;
      p.gfx.visible = true;
      p.gfx.x = x;
      p.gfx.y = y;
      p.gfx.tint = color;
      p.gfx.alpha = 1;
      p.gfx.scale.set(p.baseScale);
      p.gfx.rotation = Math.random() * Math.PI;
    }
  }

  update(dt: number): void {
    for (const p of this.pool) {
      if (!p.active) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.active = false;
        p.gfx.visible = false;
        continue;
      }
      p.vy += p.gravity * dt;
      p.gfx.x += (p.vx + WIND_DRIFT) * dt;
      p.gfx.y += p.vy * dt;
      p.gfx.rotation += p.spin * dt;
      const t = p.life / p.maxLife;
      p.gfx.alpha = t;
      p.gfx.scale.set(p.baseScale * (0.4 + t * 0.6));
    }
  }
}
